import { AnimatePresence, MotiView } from "moti";
import { memo, useEffect } from "react";
import { useRouter } from "solito/router";
import { useShallow } from "zustand/react/shallow";
import { LinearGradient } from "@tamagui/linear-gradient";
import { Circle, isWeb, Subheadline1, Subheadline3, XStack, YStack } from "@medbillai/ui";
import { loginUrl } from "@medbillai/utils";
import { GrantedLogo } from "../../../components/local-images";
import { ScreenView } from "../../../components/ScreenView";
import { BUBBLE_DELAY, BUBBLE_STAGGER, LINE_DELAY, LINE_STAGGER, LOGO_DELAY, spring } from "../../../lib/animations/motiConstants";
import { useSignUpOnboardingStore } from "../../../lib/sign-up-onboarding/signUpOnboardingStore";
import { GetStartedActionButton } from "./GetStartedActionButton";
import { GetStartedNextButton } from "./GetStartedNextButton";
import { stages } from "./getStartedConfig";
const GetStartedActionButtonMemo = memo(GetStartedActionButton);
const HeaderText = isWeb ? Subheadline1 : Subheadline3;
export const GetStartedScreen = () => {
  const {
    push
  } = useRouter();
  const userSegmentationData = useSignUpOnboardingStore(s => s.userSegmentationData);
  const {
    step,
    next,
    isLastStage,
    setIsSignUpOnboarding
  } = useSignUpOnboardingStore(useShallow(s => ({
    step: s.step,
    next: s.next,
    userSegmentationData: s.userSegmentationData,
    isLastStage: s.step === stages.length - 1,
    setIsSignUpOnboarding: s.setIsSignUpOnboarding
  })));
  const stage = stages[step]!;

  // Get header text - either static or dynamically generated
  const headerLines = stage.getHeader ? stage.getHeader(userSegmentationData) : stage.header;

  // Auto-continue for stages that have autoContinue configured
  // If the last stage has autoContinue, we need to set the isSignUpOnboarding
  // to true and push to the login page.
  useEffect(() => {
    if (stage.autoContinue && !isLastStage) {
      const timeout = setTimeout(next, stage.autoContinue);
      return () => clearTimeout(timeout);
    } else if (isLastStage && stage.autoContinue) {
      const timeout = setTimeout(() => {
        setIsSignUpOnboarding(true);
        push(loginUrl());
      }, stage.autoContinue);
      return () => clearTimeout(timeout);
    }
  }, [stage.autoContinue, next, isLastStage, setIsSignUpOnboarding, push]);
  return <>
      <ScreenView backgroundColor={isWeb ? undefined : "$background"} stackProps={{
      paddingTop: "$4.5",
      paddingHorizontal: "$4.5"
    }} widthLimit={600} data-sentry-element="ScreenView" data-sentry-source-file="get-started.tsx">
        <XStack gap="$2.5" data-sentry-element="XStack" data-sentry-source-file="get-started.tsx">
          <MotiView from={{
          opacity: 0,
          translateY: 12
        }} animate={{
          opacity: 1,
          translateY: 0
        }} transition={{
          ...spring,
          delay: LOGO_DELAY
        }} data-sentry-element="MotiView" data-sentry-source-file="get-started.tsx">
            <Circle size={48} data-sentry-element="Circle" data-sentry-source-file="get-started.tsx">
              <LinearGradient br="$12" colors={["$lightblue", "white", "$limegreen"]} start={{
              x: 0,
              y: 0.4
            }} end={{
              x: 0.8,
              y: 1
            }} pos="absolute" fullscreen data-sentry-element="LinearGradient" data-sentry-source-file="get-started.tsx" />
              <GrantedLogo height={34} width={34} zIndex={1} data-sentry-element="GrantedLogo" data-sentry-source-file="get-started.tsx" />
            </Circle>
          </MotiView>
          <YStack pr="$8" f={1} position="relative" top={isWeb ? 5 : 10} maxWidth={isWeb ? 300 : 225} data-sentry-element="YStack" data-sentry-source-file="get-started.tsx">
            <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="get-started.tsx">
              {/** External moti ensures group animates out together */}
              <MotiView
            // No from to avoid double animation
            key={stage.id} style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0
            }} animate={{
              opacity: 1,
              translateY: 0
            }} exit={{
              opacity: 0,
              translateY: -12
            }} transition={spring} exitTransition={spring} data-sentry-element="MotiView" data-sentry-source-file="get-started.tsx">
                {/* lines animate in staggered and exit together */}
                {headerLines?.map((line, i) => <MotiView
              // No exit to avoid double animation
              key={`${stage.id}-${i}`} from={{
                opacity: 0,
                translateY: 8
              }} animate={{
                opacity: 1,
                translateY: 0
              }} transition={{
                ...spring,
                delay: i * LINE_STAGGER + LINE_DELAY
              }} exitTransition={spring}>
                    <HeaderText pb="$4">{line}</HeaderText>
                  </MotiView>)}
              </MotiView>
            </AnimatePresence>
          </YStack>
        </XStack>
        {stage.choices?.options ? <YStack gap="$2" mt="$8" alignItems="flex-end" justifyContent="flex-start">
            {stage.choices.options.map((opt, idx) => <GetStartedActionButtonMemo key={opt.id} id={opt.id} icon={opt.icon} label={opt.label} stageId={stage.id} stagerDelay={idx * BUBBLE_STAGGER + BUBBLE_DELAY} />)}
          </YStack> : null}
      </ScreenView>
      <GetStartedNextButton data-sentry-element="GetStartedNextButton" data-sentry-source-file="get-started.tsx" />
    </>;
};